import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend, AreaChart, Area, FunnelChart, 
  Funnel, LabelList, RadarChart, PolarGrid, PolarAngleAxis, Radar
} from 'recharts';
import {
  Phone, PhoneOff, Clock, Users, TrendingUp, TrendingDown,
  Filter, Calendar, Download, MessageSquare, BarChart3, PieChart as PieIcon,
  Activity, AlertCircle, CheckCircle, XCircle, ChevronRight,
  Search, RefreshCw, Copy, Zap, Target, Award,
  ArrowUp, ArrowDown, Eye, List, Brain, Layers, GitCompare,
  Users2, Star, MapPin, AlertTriangle, ThermometerSun, DollarSign,
  Database, BookOpen, ChevronLeft
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { ScrollArea } from "./ui/scroll-area";
import { Label } from "./ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authAxios = axios.create();
authAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const STATUS_COLORS = {
  "KAYIT OK": "#10b981",
  "CEVAP YOK": "#ef4444",
  "MEŞGUL": "#f59e0b",
  "DÜŞÜNECEK": "#8b5cf6",
  "İSTEMİYOR": "#6b7280",
};

const CHART_COLORS = ["#10b981", "#ef4444", "#f59e0b", "#8b5cf6", "#3b82f6", "#ec4899", "#14b8a6", "#f97316"];
const formatNumber = (num) => new Intl.NumberFormat('tr-TR').format(num || 0);
const formatCurrency = (num) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(num || 0);

// Stat Card
const StatCard = ({ title, value, icon: Icon, color, trend, subtitle }) => (
  <Card className="bg-white border-slate-200">
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wide">{title}</p>
          <p className={`text-2xl font-bold ${color || "text-slate-800"}`}>{value}</p>
          {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-xl ${color?.replace("text-", "bg-").replace("400", "500/20") || "bg-slate-200"}`}>
          <Icon size={24} className={color || "text-slate-500"} />
        </div>
      </div>
      {trend !== undefined && (
        <div className={`flex items-center gap-1 mt-2 text-xs ${trend >= 0 ? "text-emerald-400" : "text-red-400"}`}>
          {trend >= 0 ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
          <span>%{Math.abs(trend)} önceki döneme göre</span>
        </div>
      )}
    </CardContent>
  </Card>
);

// Change Badge
const ChangeBadge = ({ value }) => {
  const isPositive = value >= 0;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
      isPositive ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
    }`}>
      {isPositive ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
      %{Math.abs(value)}
    </span>
  );
};

export default function DataCenter() {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  
  // Data states
  const [statusList, setStatusList] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [agentDetail, setAgentDetail] = useState(null);
  const [timeGaps, setTimeGaps] = useState(null);
  const [bulkMessageList, setBulkMessageList] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Advanced analytics states
  const [funnel, setFunnel] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [segmentation, setSegmentation] = useState(null);
  const [leadScoring, setLeadScoring] = useState(null);
  const [callbackOpt, setCallbackOpt] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [roiAnalysis, setRoiAnalysis] = useState(null);
  const [anomalies, setAnomalies] = useState(null);
  const [comparisonPeriod, setComparisonPeriod] = useState("week");
  const [segmentBy, setSegmentBy] = useState("reklam_turu");
  
  // CRM Data (Müşteri Adayları) states
  const [crmLeads, setCrmLeads] = useState([]);
  const [crmStats, setCrmStats] = useState(null);
  const [crmLoading, setCrmLoading] = useState(false);
  const [crmSyncing, setCrmSyncing] = useState(false);
  const [crmPage, setCrmPage] = useState(1);
  const [crmTotalPages, setCrmTotalPages] = useState(1);
  const [crmSearchTerm, setCrmSearchTerm] = useState("");
  const [crmStatusFilter, setCrmStatusFilter] = useState("");
  const [crmAgentFilter, setCrmAgentFilter] = useState("");
  const [crmDateRange, setCrmDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\./g, '.'),
    end: new Date().toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\./g, '.')
  });

  // Data Analysis states
  const [daData, setDaData] = useState([]);
  const [daTotal, setDaTotal] = useState(0);
  const [daPage, setDaPage] = useState(1);
  const [daTotalPages, setDaTotalPages] = useState(1);
  const [daLoading, setDaLoading] = useState(false);
  const [daStatusSummary, setDaStatusSummary] = useState([]);
  const [daAgentSummary, setDaAgentSummary] = useState([]);
  const [daDaysGroups, setDaDaysGroups] = useState([]);
  const [daFilters, setDaFilters] = useState({ agents: [], statuses: [] });
  const [daDateFrom, setDaDateFrom] = useState(() => { const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().substring(0, 10); });
  const [daDateTo, setDaDateTo] = useState(new Date().toISOString().substring(0, 10));
  const [daStatus, setDaStatus] = useState("all");
  const [daAgent, setDaAgent] = useState("all");
  const [daMinDays, setDaMinDays] = useState("");
  const [daViewMode, setDaViewMode] = useState("all"); // all, agent-detail

  const fetchDataAnalysis = async (page = 1) => {
    setDaLoading(true);
    try {
      const params = new URLSearchParams();
      if (daDateFrom) params.append("date_from", daDateFrom);
      if (daDateTo) params.append("date_to", daDateTo);
      if (daStatus && daStatus !== "all") params.append("status", daStatus);
      if (daAgent && daAgent !== "all") params.append("agent_name", daAgent);
      if (daMinDays) params.append("min_days", daMinDays);
      params.append("page", page);
      params.append("limit", 50);
      const res = await authAxios.get(`${API}/data-center/data-analysis?${params.toString()}`);
      setDaData(res.data.data);
      setDaTotal(res.data.total);
      setDaPage(res.data.page);
      setDaTotalPages(res.data.total_pages);
      setDaStatusSummary(res.data.status_summary);
      setDaAgentSummary(res.data.agent_summary);
      setDaDaysGroups(res.data.days_groups);
      setDaFilters(res.data.filters);
    } catch (err) { console.error(err); }
    setDaLoading(false);
  };


  // CRM Data fetch functions
  const fetchCrmLeads = async (page = 1) => {
    try {
      setCrmLoading(true);
      const params = new URLSearchParams({ page, limit: 30 });
      if (crmSearchTerm) params.append("search", crmSearchTerm);
      if (crmStatusFilter) params.append("status", crmStatusFilter);
      if (crmAgentFilter) params.append("agent_name", crmAgentFilter);
      
      const response = await authAxios.get(`${API}/kariyer-paneli/data/list?${params}`);
      setCrmLeads(response.data.data || []);
      setCrmTotalPages(response.data.total_pages || 1);
      setCrmPage(page);
    } catch (error) {
      toast.error("Müşteri adayları yüklenemedi");
    } finally {
      setCrmLoading(false);
    }
  };

  const fetchCrmStats = async () => {
    try {
      const response = await authAxios.get(`${API}/kariyer-paneli/data/stats`);
      setCrmStats(response.data);
    } catch (error) {
      console.error("CRM stats hatası:", error);
    }
  };

  const syncCrmData = async () => {
    try {
      setCrmSyncing(true);
      const response = await authAxios.post(`${API}/kariyer-paneli/data/sync`, {
        start_date: crmDateRange.start,
        end_date: crmDateRange.end
      });
      toast.success(response.data.message);
      fetchCrmStats();
      fetchCrmLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Senkronizasyon başarısız");
    } finally {
      setCrmSyncing(false);
    }
  };

  const getCrmStatusColor = (status) => {
    if (!status) return "bg-slate-200 text-slate-600";
    const s = status.toLowerCase();
    if (s.includes("satış") || s.includes("kayıt")) return "bg-green-100 text-green-700";
    if (s.includes("ilgisiz") || s.includes("ulaşılamadı")) return "bg-red-100 text-red-700";
    if (s.includes("düşünüyor") || s.includes("düşünecek") || s.includes("araştırıyor")) return "bg-amber-100 text-amber-700";
    if (s.includes("görüşme") || s.includes("bilgi")) return "bg-blue-100 text-blue-700";
    return "bg-slate-200 text-slate-600";
  };

  // Fetch functions
  const fetchOverview = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (selectedAgent && selectedAgent !== "all") params.append("agent_name", selectedAgent);
      const res = await authAxios.get(`${API}/data-center/overview?${params}`);
      setOverview(res.data);
    } catch (error) {
      toast.error("Veri yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, selectedAgent]);

  const fetchAgents = useCallback(async () => {
    try {
      const res = await authAxios.get(`${API}/agents`);
      setAgents(res.data || []);
    } catch (error) {}
  }, []);

  const fetchFunnel = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (selectedAgent && selectedAgent !== "all") params.append("agent_name", selectedAgent);
      const res = await authAxios.get(`${API}/data-center/conversion-funnel?${params}`);
      setFunnel(res.data);
    } catch (error) {
      toast.error("Huni verisi yüklenemedi");
    }
  }, [dateFrom, dateTo, selectedAgent]);

  const fetchComparison = useCallback(async () => {
    try {
      const params = new URLSearchParams({ period: comparisonPeriod });
      if (selectedAgent && selectedAgent !== "all") params.append("agent_name", selectedAgent);
      const res = await authAxios.get(`${API}/data-center/period-comparison?${params}`);
      setComparison(res.data);
    } catch (error) {
      toast.error("Karşılaştırma verisi yüklenemedi");
    }
  }, [comparisonPeriod, selectedAgent]);

  const fetchSegmentation = useCallback(async () => {
    try {
      const params = new URLSearchParams({ segment_by: segmentBy });
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      const res = await authAxios.get(`${API}/data-center/customer-segmentation?${params}`);
      setSegmentation(res.data);
    } catch (error) {
      toast.error("Segmentasyon verisi yüklenemedi");
    }
  }, [segmentBy, dateFrom, dateTo]);

  const fetchLeadScoring = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      const res = await authAxios.get(`${API}/data-center/lead-scoring?${params}`);
      setLeadScoring(res.data);
    } catch (error) {
      toast.error("Lead skorlama verisi yüklenemedi");
    }
  }, [dateFrom, dateTo]);

  const fetchCallbackOpt = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      const res = await authAxios.get(`${API}/data-center/callback-optimization?${params}`);
      setCallbackOpt(res.data);
    } catch (error) {
      toast.error("Geri arama verisi yüklenemedi");
    }
  }, [dateFrom, dateTo]);

  const fetchHeatmap = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (selectedAgent && selectedAgent !== "all") params.append("agent_name", selectedAgent);
      const res = await authAxios.get(`${API}/data-center/heatmap?${params}`);
      setHeatmap(res.data);
    } catch (error) {
      toast.error("Isı haritası yüklenemedi");
    }
  }, [dateFrom, dateTo, selectedAgent]);

  const fetchROI = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      const res = await authAxios.get(`${API}/data-center/roi-analysis?${params}`);
      setRoiAnalysis(res.data);
    } catch (error) {
      toast.error("ROI analizi yüklenemedi");
    }
  }, [dateFrom, dateTo]);

  const fetchAnomalies = useCallback(async () => {
    try {
      const res = await authAxios.get(`${API}/data-center/anomaly-detection?days=30`);
      setAnomalies(res.data);
    } catch (error) {
      toast.error("Anomali tespiti yüklenemedi");
    }
  }, []);

  const fetchStatusList = useCallback(async (status) => {
    try {
      const params = new URLSearchParams({ status });
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (selectedAgent && selectedAgent !== "all") params.append("agent_name", selectedAgent);
      const res = await authAxios.get(`${API}/data-center/status-list?${params}`);
      setStatusList(res.data);
      setSelectedStatus(status);
    } catch (error) {
      toast.error("Liste yüklenemedi");
    }
  }, [dateFrom, dateTo, selectedAgent]);

  const fetchAgentDetail = useCallback(async (agentName) => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      const res = await authAxios.get(`${API}/data-center/agent-detail/${encodeURIComponent(agentName)}?${params}`);
      setAgentDetail(res.data);
    } catch (error) {
      toast.error("Danışman detayı yüklenemedi");
    }
  }, [dateFrom, dateTo]);

  const fetchBulkMessageList = useCallback(async (status) => {
    try {
      const params = new URLSearchParams({ status });
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (selectedAgent && selectedAgent !== "all") params.append("agent_name", selectedAgent);
      const res = await authAxios.get(`${API}/data-center/bulk-message-list?${params}`);
      setBulkMessageList(res.data);
    } catch (error) {
      toast.error("Mesaj listesi yüklenemedi");
    }
  }, [dateFrom, dateTo, selectedAgent]);

  useEffect(() => {
    fetchAgents();
    fetchOverview();
  }, [fetchAgents, fetchOverview]);

  // Tab change handler
  useEffect(() => {
    if (activeTab === "funnel") fetchFunnel();
    else if (activeTab === "comparison") fetchComparison();
    else if (activeTab === "segmentation") fetchSegmentation();
    else if (activeTab === "leads") fetchLeadScoring();
    else if (activeTab === "callback") fetchCallbackOpt();
    else if (activeTab === "heatmap") fetchHeatmap();
    else if (activeTab === "roi") fetchROI();
    else if (activeTab === "anomaly") fetchAnomalies();
    else if (activeTab === "crm-data") { fetchCrmStats(); fetchCrmLeads(); }
    else if (activeTab === "data-analysis") { fetchDataAnalysis(); }
  }, [activeTab, fetchFunnel, fetchComparison, fetchSegmentation, fetchLeadScoring, fetchCallbackOpt, fetchHeatmap, fetchROI, fetchAnomalies]);

  const copyPhoneNumbers = () => {
    if (!bulkMessageList?.data) return;
    const phones = bulkMessageList.data.map(d => d.telefon).join("\n");
    navigator.clipboard.writeText(phones);
    toast.success(`${bulkMessageList.data.length} telefon numarası kopyalandı`);
  };

  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) return;
    const headers = Object.keys(data[0]).join(",");
    const rows = data.map(row => Object.values(row).map(v => `"${v || ""}"`).join(",")).join("\n");
    const csv = `${headers}\n${rows}`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    toast.success("CSV dosyası indirildi");
  };

  const tabs = [
    { id: "overview", label: "Genel Bakış", icon: BarChart3 },
    { id: "data-analysis", label: "Data Analiz", icon: Search },
    { id: "crm-data", label: "Müşteri Adayları", icon: Database },
    { id: "funnel", label: "Dönüşüm Hunisi", icon: Layers },
    { id: "comparison", label: "Dönem Karşılaştırma", icon: GitCompare },
    { id: "segmentation", label: "Segmentasyon", icon: Users2 },
    { id: "leads", label: "Lead Skorlama", icon: Star },
    { id: "callback", label: "Geri Arama", icon: Phone },
    { id: "heatmap", label: "Isı Haritası", icon: ThermometerSun },
    { id: "roi", label: "ROI Analizi", icon: DollarSign },
    { id: "anomaly", label: "Anomali Tespiti", icon: AlertTriangle },
    { id: "status", label: "Durum Analizi", icon: PieIcon },
    { id: "hourly", label: "Saatlik", icon: Clock },
    { id: "agents", label: "Danışmanlar", icon: Users },
    { id: "messages", label: "Toplu Mesaj", icon: MessageSquare },
  ];

  if (loading && !overview) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-emerald-400 mx-auto mb-4" />
          <p className="text-slate-500">Veriler yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6" data-testid="data-center">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-slate-800 flex items-center gap-2 md:gap-3">
            <div className="p-1.5 md:p-2 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-xl">
              <Activity className="text-cyan-400" size={20} />
            </div>
            Data Merkezi
          </h1>
          <p className="text-slate-500 text-sm mt-1 hidden sm:block">Gelişmiş arama ve satış analizi</p>
        </div>
        <Button onClick={fetchOverview} variant="outline" size="sm" className="border-slate-300 self-start sm:self-auto">
          <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Yenile
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-white border-slate-200">
        <CardContent className="p-3 md:p-4">
          <div className="grid grid-cols-2 md:flex md:flex-wrap gap-2 md:gap-4 items-end">
            <div className="col-span-2 md:flex-1 md:min-w-[200px]">
              <label className="text-xs text-slate-500 mb-1 block">Danışman</label>
              <Select value={selectedAgent} onValueChange={setSelectedAgent}>
                <SelectTrigger className="bg-white border-slate-300">
                  <SelectValue placeholder="Tüm Danışmanlar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Danışmanlar</SelectItem>
                  {agents.map(agent => (
                    <SelectItem key={agent.id} value={agent.name}>{agent.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Başlangıç</label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="bg-white border-slate-300 w-full md:w-36" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Bitiş</label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="bg-white border-slate-300 w-full md:w-36" />
            </div>
            <Button onClick={fetchOverview} className="bg-cyan-600 hover:bg-cyan-500 col-span-2 md:col-span-1">
              <Filter size={14} className="mr-1.5" />
              Filtrele
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabs - Scrollable on mobile */}
      <div className="overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0">
        <div className="flex gap-1.5 md:gap-2 min-w-max md:flex-wrap">
          {tabs.map(tab => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveTab(tab.id)}
              className={`text-xs md:text-sm whitespace-nowrap ${activeTab === tab.id ? "bg-cyan-600 text-white" : "border-slate-300 text-slate-700 hover:bg-slate-100"}`}
            >
              <tab.icon size={12} className="mr-1 md:mr-1.5" />
              <span className="hidden xs:inline">{tab.label}</span>
              <span className="xs:hidden">{tab.label.split(" ")[0]}</span>
            </Button>
          ))}
        </div>
      </div>

      {/* OVERVIEW TAB */}
      {activeTab === "overview" && overview && (
        <div className="space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard title="Toplam Arama" value={formatNumber(overview.summary?.total_calls)} icon={Phone} color="text-cyan-400" />
            <StatCard title="Toplam Satış" value={formatNumber(overview.summary?.total_sales)} icon={Target} color="text-emerald-400" />
            <StatCard title="Günlük Ortalama" value={overview.summary?.avg_calls_per_day || 0} icon={TrendingUp} color="text-amber-400" subtitle="arama/gün" />
            <StatCard title="Başarı Oranı" value={`%${overview.summary?.avg_success_rate || 0}`} icon={Award} color="text-purple-400" />
          </div>

          {/* Best/Worst Hours */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {overview.best_hour && (
              <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-emerald-500/30">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-emerald-500/20 rounded-xl"><CheckCircle className="text-emerald-400" size={28} /></div>
                    <div>
                      <p className="text-sm text-emerald-400">En Verimli Saat</p>
                      <p className="text-3xl font-bold text-slate-800">{overview.best_hour._id}:00</p>
                      <p className="text-sm text-slate-500">{overview.best_hour.successful} başarılı / {overview.best_hour.total} toplam</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            {overview.worst_hour && (
              <Card className="bg-gradient-to-br from-red-500/10 to-red-600/5 border-red-500/30">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-red-500/20 rounded-xl"><PhoneOff className="text-red-400" size={28} /></div>
                    <div>
                      <p className="text-sm text-red-400">En Çok Cevap Yok</p>
                      <p className="text-3xl font-bold text-slate-800">{overview.worst_hour._id}:00</p>
                      <p className="text-sm text-slate-500">{overview.worst_hour.cevap_yok} cevap yok / {overview.worst_hour.total} toplam</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Status Distribution + Daily Trend side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Status Distribution Pie Chart */}
            <Card className="bg-white border-slate-200">
              <CardHeader><CardTitle className="flex items-center gap-2"><PieIcon className="text-amber-400" size={20} />Durum Dağılımı</CardTitle></CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={(overview.status_distribution || []).slice(0, 8)}
                        dataKey="count"
                        nameKey="_id"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label={({ _id, percent }) => `${_id} %${(percent * 100).toFixed(0)}`}
                        labelLine={false}
                      >
                        {overview.status_distribution.slice(0, 8).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', color: '#334155' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                {/* Status counts below */}
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {(overview.status_distribution || []).slice(0, 8).map((s, i) => (
                    <div key={s._id} className="flex items-center gap-2 text-xs">
                      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                      <span className="text-slate-500 truncate">{s._id}</span>
                      <span className="text-slate-800 font-bold ml-auto">{formatNumber(s.count)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Daily Trend */}
            <Card className="bg-white border-slate-200">
              <CardHeader><CardTitle className="flex items-center gap-2"><TrendingUp className="text-cyan-400" size={20} />Günlük Trend</CardTitle></CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={[...(overview.daily_stats || [])].reverse()}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="_id" tick={{ fill: '#888', fontSize: 10 }} />
                      <YAxis tick={{ fill: '#888' }} />
                      <Tooltip contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0' }} />
                      <Area type="monotone" dataKey="total" stroke="#06b6d4" fill="#06b6d4" fillOpacity={0.2} name="Toplam" />
                      <Area type="monotone" dataKey="successful" stroke="#10b981" fill="#10b981" fillOpacity={0.2} name="Başarılı" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Agent Performance Table */}
          {overview.agent_stats && overview.agent_stats.length > 0 && (
            <Card className="bg-white border-slate-200" data-testid="agent-performance-table">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="text-emerald-400" size={20} />
                  Danışman Performans Tablosu
                </CardTitle>
                <CardDescription>Tüm danışmanların arama sayıları ve dönüşüm oranları</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <table className="w-full" data-testid="agent-stats-table">
                    <thead className="sticky top-0 bg-slate-50 z-10">
                      <tr className="text-xs text-slate-500 border-b border-slate-200 uppercase tracking-wider">
                        <th className="p-3 text-left">#</th>
                        <th className="p-3 text-left">Danışman</th>
                        <th className="p-3 text-right">Toplam Arama</th>
                        <th className="p-3 text-right">Başarılı</th>
                        <th className="p-3 text-right">Kayıt OK</th>
                        <th className="p-3 text-right hidden md:table-cell">Cevap Yok</th>
                        <th className="p-3 text-right hidden md:table-cell">Meşgul</th>
                        <th className="p-3 text-right hidden lg:table-cell">Düşünecek</th>
                        <th className="p-3 text-right hidden lg:table-cell">İstemiyor</th>
                        <th className="p-3 text-right">Dönüşüm %</th>
                        <th className="p-3 text-right hidden md:table-cell">Cevap Yok %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(overview.agent_stats || [])
                        .sort((a, b) => b.total_calls - a.total_calls)
                        .map((agent, idx) => (
                        <tr key={agent._id} className="border-b border-slate-200 hover:bg-slate-50 transition-colors">
                          <td className="p-3 text-slate-500 font-mono">{idx + 1}</td>
                          <td className="p-3">
                            <span className="text-slate-800 font-semibold">{agent._id}</span>
                          </td>
                          <td className="p-3 text-right">
                            <span className="text-cyan-400 font-bold text-lg">{formatNumber(agent.total_calls)}</span>
                          </td>
                          <td className="p-3 text-right">
                            <span className="text-emerald-400 font-bold">{formatNumber(agent.successful_calls)}</span>
                          </td>
                          <td className="p-3 text-right">
                            <span className="px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-sm font-bold">
                              {formatNumber(agent.kayit_ok || 0)}
                            </span>
                          </td>
                          <td className="p-3 text-right hidden md:table-cell">
                            <span className="text-red-400">{formatNumber(agent.cevap_yok || 0)}</span>
                          </td>
                          <td className="p-3 text-right hidden md:table-cell">
                            <span className="text-amber-400">{formatNumber(agent.mesgul || 0)}</span>
                          </td>
                          <td className="p-3 text-right hidden lg:table-cell">
                            <span className="text-purple-400">{formatNumber(agent.dusunecek || 0)}</span>
                          </td>
                          <td className="p-3 text-right hidden lg:table-cell">
                            <span className="text-slate-500">{formatNumber(agent.istemiyor || 0)}</span>
                          </td>
                          <td className="p-3 text-right">
                            <span className={`px-2 py-0.5 rounded-full text-sm font-bold ${
                              agent.conversion_rate >= 2 ? "bg-emerald-500/20 text-emerald-400" :
                              agent.conversion_rate >= 1 ? "bg-amber-500/20 text-amber-400" :
                              "bg-red-500/20 text-red-400"
                            }`}>
                              %{agent.conversion_rate}
                            </span>
                          </td>
                          <td className="p-3 text-right hidden md:table-cell">
                            <span className={`text-sm ${agent.cevap_yok_rate > 40 ? "text-red-400" : "text-slate-500"}`}>
                              %{agent.cevap_yok_rate}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    {/* Totals Row */}
                    <tfoot className="bg-slate-100 border-t-2 border-slate-300">
                      <tr className="font-bold">
                        <td className="p-3" colSpan={2}><span className="text-slate-800">TOPLAM</span></td>
                        <td className="p-3 text-right text-cyan-400 text-lg">
                          {formatNumber((overview.agent_stats || []).reduce((s, a) => s + a.total_calls, 0))}
                        </td>
                        <td className="p-3 text-right text-emerald-400">
                          {formatNumber((overview.agent_stats || []).reduce((s, a) => s + a.successful_calls, 0))}
                        </td>
                        <td className="p-3 text-right text-green-400">
                          {formatNumber((overview.agent_stats || []).reduce((s, a) => s + (a.kayit_ok || 0), 0))}
                        </td>
                        <td className="p-3 text-right text-red-400 hidden md:table-cell">
                          {formatNumber((overview.agent_stats || []).reduce((s, a) => s + (a.cevap_yok || 0), 0))}
                        </td>
                        <td className="p-3 text-right text-amber-400 hidden md:table-cell">
                          {formatNumber((overview.agent_stats || []).reduce((s, a) => s + (a.mesgul || 0), 0))}
                        </td>
                        <td className="p-3 text-right text-purple-400 hidden lg:table-cell">
                          {formatNumber((overview.agent_stats || []).reduce((s, a) => s + (a.dusunecek || 0), 0))}
                        </td>
                        <td className="p-3 text-right text-slate-500 hidden lg:table-cell">
                          {formatNumber((overview.agent_stats || []).reduce((s, a) => s + (a.istemiyor || 0), 0))}
                        </td>
                        <td className="p-3 text-right">
                          <span className="px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400 text-sm">
                            %{overview.summary?.avg_success_rate || 0}
                          </span>
                        </td>
                        <td className="p-3 text-right hidden md:table-cell">-</td>
                      </tr>
                    </tfoot>
                  </table>
                </ScrollArea>
              </CardContent>
            </Card>
          )}

          {/* Agent Calls Bar Chart */}
          {overview.agent_stats && overview.agent_stats.length > 0 && (
            <Card className="bg-white border-slate-200">
              <CardHeader><CardTitle className="flex items-center gap-2"><BarChart3 className="text-blue-400" size={20} />Danışman Arama Karşılaştırma</CardTitle></CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={[...overview.agent_stats].sort((a, b) => b.total_calls - a.total_calls)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis type="number" tick={{ fill: '#888' }} />
                      <YAxis dataKey="_id" type="category" tick={{ fill: '#ccc', fontSize: 11 }} width={120} />
                      <Tooltip contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0' }} />
                      <Bar dataKey="total_calls" fill="#06b6d4" name="Toplam Arama" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="successful_calls" fill="#10b981" name="Başarılı" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="kayit_ok" fill="#22c55e" name="Kayıt OK" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* DATA ANALYSIS TAB */}
      {activeTab === "data-analysis" && (
        <div className="space-y-4" data-testid="data-analysis-tab">
          {/* Filters */}
          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
                <div>
                  <Label className="text-xs text-slate-500">Başlangıç</Label>
                  <Input type="date" value={daDateFrom} onChange={(e) => setDaDateFrom(e.target.value)} className="bg-white border-slate-300" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Bitiş</Label>
                  <Input type="date" value={daDateTo} onChange={(e) => setDaDateTo(e.target.value)} className="bg-white border-slate-300" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Durum</Label>
                  <Select value={daStatus} onValueChange={setDaStatus}>
                    <SelectTrigger className="bg-white border-slate-300"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tüm Durumlar</SelectItem>
                      {daFilters.statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Danışman</Label>
                  <Select value={daAgent} onValueChange={setDaAgent}>
                    <SelectTrigger className="bg-white border-slate-300"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tüm Danışmanlar</SelectItem>
                      {daFilters.agents.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={() => fetchDataAnalysis(1)} className="bg-cyan-600 hover:bg-cyan-500" data-testid="da-filter-btn">
                  <Filter size={14} className="mr-1.5" />Filtrele
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Days Since Contact - Quick Filters */}
          <div className="flex flex-wrap gap-2" data-testid="da-days-groups">
            <Button variant={daMinDays === "" ? "default" : "outline"} size="sm"
              onClick={() => { setDaMinDays(""); setTimeout(() => fetchDataAnalysis(1), 100); }}
              className={daMinDays === "" ? "bg-cyan-600" : "border-slate-300"}>
              Tümü ({daTotal.toLocaleString()})
            </Button>
            {daDaysGroups.map(g => (
              <Button key={g.key} variant={String(daMinDays) === String(g.key) ? "default" : "outline"} size="sm"
                onClick={() => { setDaMinDays(g.key); setTimeout(() => fetchDataAnalysis(1), 100); }}
                className={String(daMinDays) === String(g.key) ? "bg-amber-600" : "border-slate-300"}>
                {g.label} <span className="ml-1 text-xs opacity-70">({g.count.toLocaleString()})</span>
              </Button>
            ))}
          </div>

          {/* Status Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-7 gap-2">
            {daStatusSummary.slice(0, 7).map((s, i) => (
              <Card key={s._id} className={`border-slate-200 cursor-pointer transition-all hover:scale-105 ${daStatus === s._id ? "bg-cyan-900/40 border-cyan-500/50" : "bg-white"}`}
                onClick={() => { setDaStatus(s._id); setTimeout(() => fetchDataAnalysis(1), 100); }}>
                <CardContent className="p-3 text-center">
                  <p className="text-lg font-bold text-slate-800">{s.count.toLocaleString()}</p>
                  <p className="text-[10px] text-slate-500 truncate">{s._id}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Agent Summary Table */}
          {daAgentSummary.length > 0 && (
            <Card className="bg-white border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><Users size={16} className="text-emerald-400" />Danışman Bazlı Özet</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {daAgentSummary.map(a => (
                    <div key={a._id} className={`p-3 rounded-lg border cursor-pointer transition-all hover:bg-slate-100 ${daAgent === a._id ? "bg-cyan-900/30 border-cyan-500/40" : "bg-slate-50/30 border-slate-200"}`}
                      onClick={() => { setDaAgent(a._id); setTimeout(() => fetchDataAnalysis(1), 100); }}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-slate-800 font-semibold text-sm truncate">{a._id}</span>
                        <span className="text-cyan-400 font-bold">{a.total}</span>
                      </div>
                      <div className="flex gap-2 text-[10px]">
                        <span className="text-red-400">Cvp Yok: {a.cevap_yok}</span>
                        <span className="text-green-400">Kayıt: {a.kayit_ok}</span>
                        <span className="text-purple-400">Düşncek: {a.dusunecek}</span>
                        <span className="text-slate-500">İstemyz: {a.istemiyor}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Data Table */}
          <Card className="bg-white border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center justify-between">
                <span className="text-sm">{daTotal.toLocaleString()} kayıt bulundu</span>
                {daData.length > 0 && (
                  <Button variant="outline" size="sm" onClick={() => exportToCSV(daData, "data_analiz")} className="border-slate-300">
                    <Download size={14} className="mr-1" />CSV
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {daLoading ? (
                <div className="flex items-center justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-cyan-400" /><span className="ml-2 text-slate-500">Yükleniyor...</span></div>
              ) : daData.length === 0 ? (
                <div className="text-center py-12 text-slate-500"><Database size={40} className="mx-auto mb-3 opacity-30" /><p>Kayıt bulunamadı</p></div>
              ) : (
                <>
                  <ScrollArea className="h-[500px]">
                    <table className="w-full" data-testid="da-table">
                      <thead className="sticky top-0 bg-slate-50 z-10">
                        <tr className="text-xs text-slate-500 border-b border-slate-200 uppercase">
                          <th className="p-2 text-left">Müşteri</th>
                          <th className="p-2 text-left hidden md:table-cell">Telefon</th>
                          <th className="p-2 text-left">Danışman</th>
                          <th className="p-2 text-left">Durum</th>
                          <th className="p-2 text-left hidden md:table-cell">Kurs</th>
                          <th className="p-2 text-center">Gün</th>
                          <th className="p-2 text-left hidden lg:table-cell">Not</th>
                        </tr>
                      </thead>
                      <tbody>
                        {daData.map((row, idx) => (
                          <tr key={idx} className="border-b border-slate-200 hover:bg-slate-50 transition-colors">
                            <td className="p-2 text-slate-800 font-medium text-sm">{row.customer_name || "-"}</td>
                            <td className="p-2 text-cyan-400 font-mono text-sm hidden md:table-cell">{row.phone || "-"}</td>
                            <td className="p-2 text-slate-700 text-sm">{row.agent_name || "-"}</td>
                            <td className="p-2">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                row.status === "CEVAP YOK" ? "bg-red-500/20 text-red-400" :
                                row.status === "KAYIT OK" ? "bg-green-500/20 text-green-400" :
                                row.status === "DÜŞÜNECEK" ? "bg-purple-500/20 text-purple-400" :
                                row.status === "İSTEMİYOR" ? "bg-slate-200/50 text-slate-500" :
                                row.status === "MEŞGUL" ? "bg-amber-500/20 text-amber-400" :
                                "bg-blue-500/20 text-blue-400"
                              }`}>{row.status || "-"}</span>
                            </td>
                            <td className="p-2 text-slate-500 text-sm hidden md:table-cell truncate max-w-[120px]">{row.course_name || "-"}</td>
                            <td className="p-2 text-center">
                              <span className={`px-2 py-1 rounded-lg text-xs font-bold ${
                                row.days_since_contact >= 10 ? "bg-red-500/30 text-red-300" :
                                row.days_since_contact >= 5 ? "bg-amber-500/30 text-amber-300" :
                                row.days_since_contact >= 3 ? "bg-yellow-500/30 text-yellow-300" :
                                "bg-green-500/30 text-green-300"
                              }`}>{row.days_since_contact} gün</span>
                            </td>
                            <td className="p-2 text-slate-500 text-xs hidden lg:table-cell truncate max-w-[150px]">{row.note || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </ScrollArea>
                  {/* Pagination */}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
                    <span className="text-sm text-slate-500">Sayfa {daPage} / {daTotalPages}</span>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" disabled={daPage <= 1} onClick={() => fetchDataAnalysis(daPage - 1)} className="border-slate-300">
                        <ChevronLeft size={14} className="mr-1" />Önceki
                      </Button>
                      <Button variant="outline" size="sm" disabled={daPage >= daTotalPages} onClick={() => fetchDataAnalysis(daPage + 1)} className="border-slate-300">
                        Sonraki<ChevronRight size={14} className="ml-1" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* CRM DATA TAB - Müşteri Adayları */}
      {activeTab === "crm-data" && (
        <div className="space-y-6" data-testid="crm-data-tab">
          {/* CRM Stats */}
          {crmStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard title="Toplam Kayıt" value={formatNumber(crmStats.total_records)} icon={Database} color="text-cyan-400" />
              <StatCard title="Benzersiz Müşteri" value={formatNumber(crmStats.unique_customers)} icon={Users} color="text-emerald-400" />
              <StatCard title="Danışman Sayısı" value={formatNumber(crmStats.unique_agents)} icon={Users} color="text-amber-400" />
              <StatCard title="Son Senkronizasyon" value={crmStats.last_sync ? new Date(crmStats.last_sync).toLocaleDateString('tr-TR') : "Yok"} icon={RefreshCw} color="text-purple-400" />
            </div>
          )}

          {/* Sync Controls */}
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="text-cyan-400" size={20} />
                CRM Veri Senkronizasyonu
              </CardTitle>
              <CardDescription>KariyerPaneli CRM'den müşteri adayı verilerini çekin</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-3 items-end">
                <div>
                  <Label className="text-xs text-slate-500">Başlangıç</Label>
                  <Input
                    value={crmDateRange.start}
                    onChange={(e) => setCrmDateRange(prev => ({ ...prev, start: e.target.value }))}
                    placeholder="01.01.2026"
                    className="bg-white border-slate-300 w-36"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Bitiş</Label>
                  <Input
                    value={crmDateRange.end}
                    onChange={(e) => setCrmDateRange(prev => ({ ...prev, end: e.target.value }))}
                    placeholder="28.02.2026"
                    className="bg-white border-slate-300 w-36"
                  />
                </div>
                <Button onClick={syncCrmData} disabled={crmSyncing} className="bg-cyan-600 hover:bg-cyan-500" data-testid="crm-sync-btn">
                  <RefreshCw size={14} className={`mr-1.5 ${crmSyncing ? "animate-spin" : ""}`} />
                  {crmSyncing ? "Senkronize Ediliyor..." : "Senkronize Et"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Filters */}
          <Card className="bg-white border-slate-200">
            <CardContent className="p-3 md:p-4">
              <div className="grid grid-cols-2 md:flex md:flex-wrap gap-2 md:gap-4 items-end">
                <div className="col-span-2 md:flex-1">
                  <Label className="text-xs text-slate-500">Ara</Label>
                  <Input
                    placeholder="Müşteri adı veya telefon..."
                    value={crmSearchTerm}
                    onChange={(e) => setCrmSearchTerm(e.target.value)}
                    className="bg-white border-slate-300"
                    data-testid="crm-search-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Durum</Label>
                  <Select value={crmStatusFilter || "all"} onValueChange={(v) => setCrmStatusFilter(v === "all" ? "" : v)}>
                    <SelectTrigger className="bg-white border-slate-300 w-40">
                      <SelectValue placeholder="Tüm Durumlar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tüm Durumlar</SelectItem>
                      <SelectItem value="KAYIT OK">Kayıt OK</SelectItem>
                      <SelectItem value="DÜŞÜNECEK">Düşünecek</SelectItem>
                      <SelectItem value="CEVAP YOK">Cevap Yok</SelectItem>
                      <SelectItem value="İSTEMİYOR">İstemiyor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Danışman</Label>
                  <Select value={crmAgentFilter || "all"} onValueChange={(v) => setCrmAgentFilter(v === "all" ? "" : v)}>
                    <SelectTrigger className="bg-white border-slate-300 w-40">
                      <SelectValue placeholder="Tüm Danışmanlar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tüm Danışmanlar</SelectItem>
                      {agents.map(a => <SelectItem key={a.id} value={a.name}>{a.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={() => fetchCrmLeads(1)} className="bg-cyan-600 hover:bg-cyan-500 col-span-2 md:col-span-1" data-testid="crm-filter-btn">
                  <Filter size={14} className="mr-1.5" />
                  Filtrele
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* CRM Leads Table */}
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Müşteri Adayları Listesi</span>
                {crmLeads.length > 0 && (
                  <Button variant="outline" size="sm" onClick={() => exportToCSV(crmLeads, "musteri_adaylari")} className="border-slate-300" data-testid="crm-export-btn">
                    <Download size={14} className="mr-1" />
                    CSV
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {crmLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-6 h-6 animate-spin text-cyan-400" />
                  <span className="ml-2 text-slate-500">Yükleniyor...</span>
                </div>
              ) : crmLeads.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Database size={40} className="mx-auto mb-3 opacity-30" />
                  <p>Henüz veri yok. Yukarıdan senkronize edin.</p>
                </div>
              ) : (
                <>
                  <ScrollArea className="h-[500px]">
                    <table className="w-full" data-testid="crm-leads-table">
                      <thead className="sticky top-0 bg-slate-50 z-10">
                        <tr className="text-xs text-slate-500 border-b border-slate-200">
                          <th className="p-2 text-left">Müşteri</th>
                          <th className="p-2 text-left hidden md:table-cell">Telefon</th>
                          <th className="p-2 text-left hidden lg:table-cell">Danışman</th>
                          <th className="p-2 text-left">Durum</th>
                          <th className="p-2 text-left hidden md:table-cell">Ders</th>
                          <th className="p-2 text-left hidden lg:table-cell">Tarih</th>
                        </tr>
                      </thead>
                      <tbody>
                        {crmLeads.map((lead, idx) => (
                          <tr key={idx} className="border-b border-slate-200 hover:bg-slate-50 transition-colors">
                            <td className="p-2 text-slate-800 font-medium">{lead.customer_name || lead.musteri_adi || "-"}</td>
                            <td className="p-2 text-cyan-400 font-mono hidden md:table-cell">{lead.phone || lead.telefon || "-"}</td>
                            <td className="p-2 text-slate-500 hidden lg:table-cell">{lead.agent_name || lead.danismani || "-"}</td>
                            <td className="p-2">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getCrmStatusColor(lead.status || lead.son_durum)}`}>
                                {lead.status || lead.son_durum || "-"}
                              </span>
                            </td>
                            <td className="p-2 text-slate-500 text-sm hidden md:table-cell">{lead.course || lead.ders_adi || "-"}</td>
                            <td className="p-2 text-slate-500 text-sm hidden lg:table-cell">{lead.created_at ? new Date(lead.created_at).toLocaleDateString('tr-TR') : lead.tarih || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </ScrollArea>

                  {/* Pagination */}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
                    <span className="text-sm text-slate-500">Sayfa {crmPage} / {crmTotalPages}</span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={crmPage <= 1}
                        onClick={() => fetchCrmLeads(crmPage - 1)}
                        className="border-slate-300"
                        data-testid="crm-prev-page"
                      >
                        <ChevronLeft size={14} className="mr-1" />
                        Önceki
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={crmPage >= crmTotalPages}
                        onClick={() => fetchCrmLeads(crmPage + 1)}
                        className="border-slate-300"
                        data-testid="crm-next-page"
                      >
                        Sonraki
                        <ChevronRight size={14} className="ml-1" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* FUNNEL TAB */}
      {activeTab === "funnel" && (
        <div className="space-y-6">
          {funnel ? (
            <>
              <div className="grid grid-cols-4 gap-4">
                {funnel.funnel.map((stage, idx) => (
                  <Card key={stage.stage} className="bg-white border-slate-200">
                    <CardContent className="p-4 text-center">
                      <p className="text-xs text-slate-500 mb-1">{stage.stage}</p>
                      <p className="text-3xl font-bold text-slate-800">{formatNumber(stage.count)}</p>
                      {idx > 0 && <p className="text-sm text-cyan-400 mt-1">%{stage.rate}</p>}
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              <Card className="bg-white border-slate-200">
                <CardHeader><CardTitle>Dönüşüm Hunisi Grafiği</CardTitle></CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={funnel.funnel} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                        <XAxis type="number" tick={{ fill: '#888' }} />
                        <YAxis dataKey="stage" type="category" tick={{ fill: '#888' }} width={100} />
                        <Tooltip contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0' }} />
                        <Bar dataKey="count" fill="#06b6d4" radius={[0, 4, 4, 0]}>
                          {funnel.funnel.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 border-purple-500/30">
                <CardHeader><CardTitle className="flex items-center gap-2"><Brain className="text-purple-400" size={20} />Dönüşüm Önerileri</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="p-4 bg-slate-100 rounded-xl">
                      <p className="text-2xl font-bold text-cyan-400">%{funnel.conversion_rates.call_to_interest}</p>
                      <p className="text-sm text-slate-500">Arama → İlgi</p>
                    </div>
                    <div className="p-4 bg-slate-100 rounded-xl">
                      <p className="text-2xl font-bold text-purple-400">%{funnel.conversion_rates.interest_to_register}</p>
                      <p className="text-sm text-slate-500">İlgi → Kayıt</p>
                    </div>
                    <div className="p-4 bg-slate-100 rounded-xl">
                      <p className="text-2xl font-bold text-emerald-400">%{funnel.conversion_rates.overall}</p>
                      <p className="text-sm text-slate-500">Genel Dönüşüm</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : <div className="text-center text-slate-500 py-12"><RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />Yükleniyor...</div>}
        </div>
      )}

      {/* COMPARISON TAB */}
      {activeTab === "comparison" && (
        <div className="space-y-6">
          <div className="flex gap-2">
            <Button onClick={() => { setComparisonPeriod("week"); }} variant={comparisonPeriod === "week" ? "default" : "outline"} className={comparisonPeriod === "week" ? "bg-cyan-600" : "border-slate-300"}>Haftalık</Button>
            <Button onClick={() => { setComparisonPeriod("month"); }} variant={comparisonPeriod === "month" ? "default" : "outline"} className={comparisonPeriod === "month" ? "bg-cyan-600" : "border-slate-300"}>Aylık</Button>
          </div>
          
          {comparison ? (
            <>
              <div className="grid grid-cols-2 gap-6">
                <Card className="bg-white border-slate-200">
                  <CardHeader><CardTitle className="text-cyan-400">{comparison.current.label}</CardTitle><CardDescription>{comparison.current.start} - {comparison.current.end}</CardDescription></CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between"><span className="text-slate-500">Arama</span><span className="text-slate-800 font-bold">{formatNumber(comparison.current.calls)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Satış</span><span className="text-slate-800 font-bold">{formatNumber(comparison.current.sales)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Ciro</span><span className="text-slate-800 font-bold">{formatCurrency(comparison.current.revenue)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Kayıt OK</span><span className="text-slate-800 font-bold">{formatNumber(comparison.current.success)}</span></div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white border-slate-200">
                  <CardHeader><CardTitle className="text-slate-500">{comparison.previous.label}</CardTitle><CardDescription>{comparison.previous.start} - {comparison.previous.end}</CardDescription></CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between"><span className="text-slate-500">Arama</span><span className="text-slate-700">{formatNumber(comparison.previous.calls)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Satış</span><span className="text-slate-700">{formatNumber(comparison.previous.sales)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Ciro</span><span className="text-slate-700">{formatCurrency(comparison.previous.revenue)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Kayıt OK</span><span className="text-slate-700">{formatNumber(comparison.previous.success)}</span></div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              <Card className="bg-white border-slate-200">
                <CardHeader><CardTitle>Değişim Oranları</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-slate-100 rounded-xl">
                      <p className="text-sm text-slate-500 mb-2">Arama</p>
                      <ChangeBadge value={comparison.changes.calls} />
                    </div>
                    <div className="text-center p-4 bg-slate-100 rounded-xl">
                      <p className="text-sm text-slate-500 mb-2">Satış</p>
                      <ChangeBadge value={comparison.changes.sales} />
                    </div>
                    <div className="text-center p-4 bg-slate-100 rounded-xl">
                      <p className="text-sm text-slate-500 mb-2">Ciro</p>
                      <ChangeBadge value={comparison.changes.revenue} />
                    </div>
                    <div className="text-center p-4 bg-slate-100 rounded-xl">
                      <p className="text-sm text-slate-500 mb-2">Kayıt</p>
                      <ChangeBadge value={comparison.changes.success} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : <div className="text-center text-slate-500 py-12"><RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />Yükleniyor...</div>}
        </div>
      )}

      {/* SEGMENTATION TAB */}
      {activeTab === "segmentation" && (
        <div className="space-y-6">
          <div className="flex gap-2">
            <Button onClick={() => setSegmentBy("reklam_turu")} variant={segmentBy === "reklam_turu" ? "default" : "outline"} className={segmentBy === "reklam_turu" ? "bg-cyan-600" : "border-slate-300"}>Reklam Kaynağı</Button>
            <Button onClick={() => setSegmentBy("egitim_durumu")} variant={segmentBy === "egitim_durumu" ? "default" : "outline"} className={segmentBy === "egitim_durumu" ? "bg-cyan-600" : "border-slate-300"}>Eğitim Durumu</Button>
            <Button onClick={() => setSegmentBy("ders_adi")} variant={segmentBy === "ders_adi" ? "default" : "outline"} className={segmentBy === "ders_adi" ? "bg-cyan-600" : "border-slate-300"}>Ders Adı</Button>
            <Button onClick={fetchSegmentation} variant="outline" className="border-slate-300 ml-auto"><RefreshCw size={14} className="mr-1" />Yenile</Button>
          </div>
          
          {segmentation ? (
            <>
              {segmentation.insights.best_segment && (
                <div className="grid grid-cols-2 gap-4">
                  <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-emerald-500/30">
                    <CardContent className="p-4">
                      <p className="text-sm text-emerald-400">En Verimli Segment</p>
                      <p className="text-2xl font-bold text-slate-800">{segmentation.insights.best_segment.name}</p>
                      <p className="text-sm text-slate-500">%{segmentation.insights.best_segment.rate} dönüşüm</p>
                    </CardContent>
                  </Card>
                  {segmentation.insights.worst_segment && (
                    <Card className="bg-gradient-to-br from-red-500/10 to-red-600/5 border-red-500/30">
                      <CardContent className="p-4">
                        <p className="text-sm text-red-400">En Az Verimli Segment</p>
                        <p className="text-2xl font-bold text-slate-800">{segmentation.insights.worst_segment.name}</p>
                        <p className="text-sm text-slate-500">%{segmentation.insights.worst_segment.rate} dönüşüm</p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
              
              <Card className="bg-white border-slate-200">
                <CardHeader><CardTitle>Segment Performansı</CardTitle></CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={segmentation.segments.slice(0, 10)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                        <XAxis type="number" tick={{ fill: '#888' }} />
                        <YAxis dataKey="name" type="category" tick={{ fill: '#888', fontSize: 11 }} width={120} />
                        <Tooltip contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0' }} />
                        <Bar dataKey="total_calls" fill="#06b6d4" name="Arama" />
                        <Bar dataKey="kayit_ok" fill="#10b981" name="Kayıt" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200">
                <CardHeader><CardTitle>Detaylı Segment Tablosu</CardTitle></CardHeader>
                <CardContent>
                  <ScrollArea className="h-64">
                    <table className="w-full">
                      <thead className="sticky top-0 bg-slate-50">
                        <tr className="text-xs text-slate-500 border-b border-slate-200">
                          <th className="p-2 text-left">Segment</th>
                          <th className="p-2 text-right">Arama</th>
                          <th className="p-2 text-right">Kayıt OK</th>
                          <th className="p-2 text-right">Düşünecek</th>
                          <th className="p-2 text-right">Cevap Yok</th>
                          <th className="p-2 text-right">Dönüşüm</th>
                        </tr>
                      </thead>
                      <tbody>
                        {segmentation.segments.map((seg, idx) => (
                          <tr key={idx} className="border-b border-slate-200 hover:bg-slate-50">
                            <td className="p-2 text-slate-800 font-medium">{seg.name}</td>
                            <td className="p-2 text-right text-slate-700">{formatNumber(seg.total_calls)}</td>
                            <td className="p-2 text-right text-emerald-400">{seg.kayit_ok}</td>
                            <td className="p-2 text-right text-purple-400">{seg.dusunecek}</td>
                            <td className="p-2 text-right text-red-400">{seg.cevap_yok}</td>
                            <td className="p-2 text-right"><span className={`px-2 py-0.5 rounded text-xs ${seg.conversion_rate >= 5 ? "bg-emerald-500/20 text-emerald-400" : "bg-slate-200 text-slate-600"}`}>%{seg.conversion_rate}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </ScrollArea>
                </CardContent>
              </Card>
            </>
          ) : <div className="text-center text-slate-500 py-12"><RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />Yükleniyor...</div>}
        </div>
      )}

      {/* LEAD SCORING TAB */}
      {activeTab === "leads" && (
        <div className="space-y-6">
          {leadScoring ? (
            <>
              <div className="grid grid-cols-4 gap-4">
                <StatCard title="Toplam Lead" value={leadScoring.total} icon={Users} color="text-cyan-400" />
                <StatCard title="Yüksek Öncelik" value={leadScoring.high_priority} icon={Star} color="text-amber-400" />
                <StatCard title="Orta Öncelik" value={leadScoring.medium_priority} icon={Target} color="text-blue-400" />
                <StatCard title="Düşük Öncelik" value={leadScoring.low_priority} icon={Clock} color="text-slate-500" />
              </div>
              
              <Card className="bg-white border-slate-200">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle className="flex items-center gap-2"><Star className="text-amber-400" size={20} />Yüksek Potansiyelli Müşteriler</CardTitle>
                    <Button onClick={() => exportToCSV(leadScoring.leads, "lead_skorlama")} variant="outline" size="sm" className="border-slate-300"><Download size={14} className="mr-1" />CSV</Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-96">
                    <table className="w-full">
                      <thead className="sticky top-0 bg-slate-50">
                        <tr className="text-xs text-slate-500 border-b border-slate-200">
                          <th className="p-2 text-left">Skor</th>
                          <th className="p-2 text-left">Müşteri</th>
                          <th className="p-2 text-left">Telefon</th>
                          <th className="p-2 text-left">Durum</th>
                          <th className="p-2 text-left">Danışman</th>
                          <th className="p-2 text-left">Son Arama</th>
                          <th className="p-2 text-left">Öncelik</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leadScoring.leads.map((lead, idx) => (
                          <tr key={idx} className="border-b border-slate-200 hover:bg-slate-50">
                            <td className="p-2">
                              <div className="flex items-center gap-2">
                                <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold" style={{
                                  background: `conic-gradient(${lead.score >= 80 ? '#f59e0b' : lead.score >= 60 ? '#3b82f6' : '#6b7280'} ${lead.score}%, #27272a ${lead.score}%)`
                                }}>
                                  <span className="bg-slate-50 w-8 h-8 rounded-full flex items-center justify-center text-slate-800">{lead.score}</span>
                                </div>
                              </div>
                            </td>
                            <td className="p-2 text-slate-800">{lead.customer_name || "-"}</td>
                            <td className="p-2 text-cyan-400 font-mono text-sm">{lead.telefon}</td>
                            <td className="p-2"><span className="px-2 py-0.5 rounded text-xs bg-purple-500/20 text-purple-400">{lead.status}</span></td>
                            <td className="p-2 text-slate-500">{lead.agent_name}</td>
                            <td className="p-2 text-slate-500 text-sm">{lead.last_call?.split("T")[0]}</td>
                            <td className="p-2"><span className={`px-2 py-0.5 rounded text-xs ${
                              lead.priority === "Yüksek" ? "bg-amber-500/20 text-amber-400" :
                              lead.priority === "Orta" ? "bg-blue-500/20 text-blue-400" :
                              "bg-slate-200 text-slate-600"
                            }`}>{lead.priority}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </ScrollArea>
                </CardContent>
              </Card>
            </>
          ) : <div className="text-center text-slate-500 py-12"><RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />Yükleniyor...</div>}
        </div>
      )}

      {/* CALLBACK OPTIMIZATION TAB */}
      {activeTab === "callback" && (
        <div className="space-y-6">
          {callbackOpt ? (
            <>
              <Card className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border-cyan-500/30">
                <CardHeader><CardTitle className="flex items-center gap-2"><Brain className="text-cyan-400" size={20} />Geri Arama Önerileri</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {callbackOpt.recommendations.filter(r => r).map((rec, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-slate-100 rounded-lg">
                        <Zap className="text-amber-400 mt-0.5" size={18} />
                        <p className="text-slate-800">{rec}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-2 gap-6">
                <Card className="bg-white border-slate-200">
                  <CardHeader><CardTitle>En İyi Geri Arama Saatleri</CardTitle></CardHeader>
                  <CardContent>
                    {callbackOpt.best_hours.length > 0 ? (
                      <div className="space-y-3">
                        {callbackOpt.best_hours.map((h, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-slate-100 rounded-lg">
                            <div className="flex items-center gap-3">
                              <span className="text-2xl font-bold text-slate-800">{h.hour}:00</span>
                              <span className="text-slate-500">({h.total} arama)</span>
                            </div>
                            <span className="text-emerald-400 font-bold">%{h.success_rate}</span>
                          </div>
                        ))}
                      </div>
                    ) : <p className="text-slate-500">Yeterli veri yok</p>}
                  </CardContent>
                </Card>

                <Card className="bg-white border-slate-200">
                  <CardHeader><CardTitle>En İyi Günler</CardTitle></CardHeader>
                  <CardContent>
                    {callbackOpt.best_days.length > 0 ? (
                      <div className="space-y-3">
                        {callbackOpt.best_days.map((d, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-slate-100 rounded-lg">
                            <span className="text-xl font-bold text-slate-800">{d.day}</span>
                            <span className="text-emerald-400 font-bold">%{d.success_rate}</span>
                          </div>
                        ))}
                      </div>
                    ) : <p className="text-slate-500">Yeterli veri yok</p>}
                  </CardContent>
                </Card>
              </div>
            </>
          ) : <div className="text-center text-slate-500 py-12"><RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />Yükleniyor...</div>}
        </div>
      )}

      {/* HEATMAP TAB */}
      {activeTab === "heatmap" && (
        <div className="space-y-6">
          {heatmap ? (
            <>
              {heatmap.best_slot && (
                <div className="grid grid-cols-2 gap-4">
                  <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-emerald-500/30">
                    <CardContent className="p-4">
                      <p className="text-sm text-emerald-400">En Verimli Zaman</p>
                      <p className="text-2xl font-bold text-slate-800">{heatmap.best_slot.day} {heatmap.best_slot.hour}</p>
                      <p className="text-sm text-slate-500">%{heatmap.best_slot.success_rate} başarı oranı</p>
                    </CardContent>
                  </Card>
                  {heatmap.worst_slot && (
                    <Card className="bg-gradient-to-br from-red-500/10 to-red-600/5 border-red-500/30">
                      <CardContent className="p-4">
                        <p className="text-sm text-red-400">Kaçınılması Gereken</p>
                        <p className="text-2xl font-bold text-slate-800">{heatmap.worst_slot.day} {heatmap.worst_slot.hour}</p>
                        <p className="text-sm text-slate-500">%{heatmap.worst_slot.success_rate} başarı oranı</p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

              <Card className="bg-white border-slate-200">
                <CardHeader><CardTitle>Gün-Saat Isı Haritası</CardTitle><CardDescription>Yeşil = yüksek başarı, Kırmızı = düşük başarı</CardDescription></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-25 gap-1">
                    <div className="col-span-1"></div>
                    {Array.from({ length: 24 }, (_, i) => (
                      <div key={i} className="text-center text-xs text-slate-500">{i}</div>
                    ))}
                    {["Pazar", "Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi"].map((day, dayIdx) => (
                      <React.Fragment key={day}>
                        <div className="text-xs text-slate-500 pr-2">{day.slice(0, 3)}</div>
                        {Array.from({ length: 24 }, (_, hour) => {
                          const cell = heatmap.heatmap.find(h => h.dayIndex === dayIdx + 1 && h.hour === hour);
                          const rate = cell?.success_rate || 0;
                          const total = cell?.total || 0;
                          const color = total < 5 ? "bg-slate-200" : rate >= 10 ? "bg-emerald-500" : rate >= 5 ? "bg-emerald-700" : rate >= 2 ? "bg-amber-600" : "bg-red-600";
                          return (
                            <div
                              key={hour}
                              className={`w-full aspect-square rounded ${color} opacity-${total < 5 ? 30 : 80}`}
                              title={`${day} ${hour}:00 - ${total} arama, %${rate} başarı`}
                            />
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </div>
                  <div className="flex justify-center gap-4 mt-4 text-xs">
                    <span className="flex items-center gap-1"><div className="w-3 h-3 bg-emerald-500 rounded" /> %10+</span>
                    <span className="flex items-center gap-1"><div className="w-3 h-3 bg-emerald-700 rounded" /> %5-10</span>
                    <span className="flex items-center gap-1"><div className="w-3 h-3 bg-amber-600 rounded" /> %2-5</span>
                    <span className="flex items-center gap-1"><div className="w-3 h-3 bg-red-600 rounded" /> %0-2</span>
                    <span className="flex items-center gap-1"><div className="w-3 h-3 bg-slate-200 rounded" /> Az veri</span>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : <div className="text-center text-slate-500 py-12"><RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />Yükleniyor...</div>}
        </div>
      )}

      {/* ROI ANALYSIS TAB */}
      {activeTab === "roi" && (
        <div className="space-y-6">
          {roiAnalysis ? (
            <>
              {roiAnalysis.best_source && (
                <div className="grid grid-cols-2 gap-4">
                  <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-emerald-500/30">
                    <CardContent className="p-4">
                      <p className="text-sm text-emerald-400">En Verimli Kaynak</p>
                      <p className="text-2xl font-bold text-slate-800">{roiAnalysis.best_source.source}</p>
                      <p className="text-sm text-slate-500">%{roiAnalysis.best_source.conversion_rate} dönüşüm, {roiAnalysis.best_source.registrations} kayıt</p>
                    </CardContent>
                  </Card>
                  {roiAnalysis.worst_source && (
                    <Card className="bg-gradient-to-br from-red-500/10 to-red-600/5 border-red-500/30">
                      <CardContent className="p-4">
                        <p className="text-sm text-red-400">En Az Verimli Kaynak</p>
                        <p className="text-2xl font-bold text-slate-800">{roiAnalysis.worst_source.source}</p>
                        <p className="text-sm text-slate-500">%{roiAnalysis.worst_source.conversion_rate} dönüşüm</p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

              <Card className="bg-white border-slate-200">
                <CardHeader><CardTitle>Reklam Kaynağı Performansı</CardTitle></CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={roiAnalysis.sources.slice(0, 10)}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                        <XAxis dataKey="source" tick={{ fill: '#888', fontSize: 10 }} angle={-45} textAnchor="end" height={80} />
                        <YAxis tick={{ fill: '#888' }} />
                        <Tooltip contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0' }} />
                        <Legend />
                        <Bar dataKey="total_calls" fill="#06b6d4" name="Arama" />
                        <Bar dataKey="registrations" fill="#10b981" name="Kayıt" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200">
                <CardHeader><CardTitle>Detaylı ROI Tablosu</CardTitle></CardHeader>
                <CardContent>
                  <ScrollArea className="h-64">
                    <table className="w-full">
                      <thead className="sticky top-0 bg-slate-50">
                        <tr className="text-xs text-slate-500 border-b border-slate-200">
                          <th className="p-2 text-left">Kaynak</th>
                          <th className="p-2 text-right">Arama</th>
                          <th className="p-2 text-right">Müşteri</th>
                          <th className="p-2 text-right">Kayıt</th>
                          <th className="p-2 text-right">Dönüşüm</th>
                          <th className="p-2 text-right">Arama/Satış</th>
                        </tr>
                      </thead>
                      <tbody>
                        {roiAnalysis.sources.map((src, idx) => (
                          <tr key={idx} className="border-b border-slate-200 hover:bg-slate-50">
                            <td className="p-2 text-slate-800 font-medium">{src.source}</td>
                            <td className="p-2 text-right text-slate-700">{formatNumber(src.total_calls)}</td>
                            <td className="p-2 text-right text-slate-700">{formatNumber(src.unique_customers)}</td>
                            <td className="p-2 text-right text-emerald-400">{src.registrations}</td>
                            <td className="p-2 text-right"><span className={`px-2 py-0.5 rounded text-xs ${src.conversion_rate >= 5 ? "bg-emerald-500/20 text-emerald-400" : "bg-slate-200 text-slate-600"}`}>%{src.conversion_rate}</span></td>
                            <td className="p-2 text-right text-slate-500">{src.calls_per_sale || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </ScrollArea>
                </CardContent>
              </Card>
            </>
          ) : <div className="text-center text-slate-500 py-12"><RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />Yükleniyor...</div>}
        </div>
      )}

      {/* ANOMALY DETECTION TAB */}
      {activeTab === "anomaly" && (
        <div className="space-y-6">
          {anomalies ? (
            <>
              <div className="grid grid-cols-3 gap-4">
                <StatCard title="Günlük Ort. Arama" value={anomalies.average_daily_calls} icon={Phone} color="text-cyan-400" />
                <StatCard title="Günlük Ort. Başarı" value={anomalies.average_daily_success} icon={CheckCircle} color="text-emerald-400" />
                <StatCard title="Anomali Sayısı" value={anomalies.total_anomalies} icon={AlertTriangle} color="text-amber-400" />
              </div>

              {anomalies.anomalies.length > 0 && (
                <Card className="bg-gradient-to-br from-amber-500/10 to-red-500/10 border-amber-500/30">
                  <CardHeader><CardTitle className="flex items-center gap-2"><AlertTriangle className="text-amber-400" size={20} />Tespit Edilen Anomaliler</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {anomalies.anomalies.map((a, idx) => (
                        <div key={idx} className={`p-4 rounded-lg border ${a.severity === "high" ? "bg-red-500/10 border-red-500/30" : "bg-amber-500/10 border-amber-500/30"}`}>
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium text-slate-800">{a.date}</p>
                              <p className="text-sm text-slate-500">{a.total_calls} arama, {a.success} başarılı</p>
                            </div>
                            <span className={`px-2 py-1 rounded text-xs ${a.severity === "high" ? "bg-red-500/20 text-red-400" : "bg-amber-500/20 text-amber-400"}`}>
                              {a.severity === "high" ? "Yüksek" : "Orta"}
                            </span>
                          </div>
                          <ul className="mt-2 space-y-1">
                            {a.issues.map((issue, i) => (
                              <li key={i} className="text-sm text-slate-700 flex items-center gap-2">
                                <AlertCircle size={14} className="text-amber-400" />
                                {issue}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card className="bg-white border-slate-200">
                <CardHeader><CardTitle>Son 30 Gün Performans</CardTitle></CardHeader>
                <CardContent>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={anomalies.daily_data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                        <XAxis dataKey="date" tick={{ fill: '#888', fontSize: 10 }} />
                        <YAxis tick={{ fill: '#888' }} />
                        <Tooltip contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0' }} />
                        <Legend />
                        <Line type="monotone" dataKey="calls" stroke="#06b6d4" name="Arama" dot={false} />
                        <Line type="monotone" dataKey="success" stroke="#10b981" name="Başarılı" dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : <div className="text-center text-slate-500 py-12"><RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />Yükleniyor...</div>}
        </div>
      )}

      {/* STATUS TAB */}
      {activeTab === "status" && overview && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-white border-slate-200">
              <CardHeader><CardTitle>Durum Dağılımı</CardTitle></CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={overview.status_distribution.slice(0, 8)} dataKey="count" nameKey="_id" cx="50%" cy="50%" outerRadius={100} label={({ _id, count }) => `${_id}: ${count}`} labelLine={false}>
                        {overview.status_distribution.slice(0, 8).map((entry, index) => (
                          <Cell key={entry._id} fill={STATUS_COLORS[entry._id] || CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border-slate-200">
              <CardHeader><CardTitle>Durum Listesi</CardTitle></CardHeader>
              <CardContent>
                <ScrollArea className="h-80">
                  <div className="space-y-2">
                    {overview.status_distribution.map((status, idx) => {
                      const percentage = ((status.count / overview.summary.total_calls) * 100).toFixed(1);
                      const color = STATUS_COLORS[status._id] || CHART_COLORS[idx % CHART_COLORS.length];
                      return (
                        <button key={status._id} onClick={() => fetchStatusList(status._id)} className={`w-full p-3 rounded-lg bg-slate-100 hover:bg-slate-200 border border-slate-300 text-left ${selectedStatus === status._id ? "ring-2 ring-cyan-500" : ""}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                              <span className="text-slate-800 font-medium">{status._id || "Belirsiz"}</span>
                            </div>
                            <div className="text-right">
                              <span className="text-slate-800 font-bold">{formatNumber(status.count)}</span>
                              <span className="text-slate-500 text-sm ml-2">({percentage}%)</span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {statusList && (
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>{selectedStatus} - {formatNumber(statusList.total)} Kayıt</CardTitle>
                  <Button variant="outline" size="sm" onClick={() => exportToCSV(statusList.data, selectedStatus)} className="border-slate-300"><Download size={14} className="mr-1" />CSV</Button>
                </div>
              </CardHeader>
              <CardContent>
                <Input placeholder="Ara..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="bg-white border-slate-300 mb-4" />
                <ScrollArea className="h-64">
                  <table className="w-full">
                    <thead className="sticky top-0 bg-slate-50">
                      <tr className="text-xs text-slate-500 border-b border-slate-200">
                        <th className="p-2 text-left">Müşteri</th>
                        <th className="p-2">Danışman</th>
                        <th className="p-2">Telefon</th>
                        <th className="p-2">Tarih</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statusList.data.filter(item => !searchTerm || item.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) || item.telefon?.includes(searchTerm)).map((item, idx) => (
                        <tr key={idx} className="border-b border-slate-200 hover:bg-slate-50">
                          <td className="p-2 text-slate-800">{item.customer_name || "-"}</td>
                          <td className="p-2 text-slate-500">{item.agent_name}</td>
                          <td className="p-2 text-cyan-400 font-mono text-sm">{item.telefon}</td>
                          <td className="p-2 text-slate-500 text-sm">{item.timestamp?.split("T")[0]}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollArea>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* HOURLY TAB */}
      {activeTab === "hourly" && overview && (
        <Card className="bg-white border-slate-200">
          <CardHeader><CardTitle>Saatlik Arama Dağılımı</CardTitle></CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={overview.hourly_stats}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="_id" tick={{ fill: '#888' }} tickFormatter={(v) => `${v}:00`} />
                  <YAxis tick={{ fill: '#888' }} />
                  <Tooltip contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0' }} />
                  <Legend />
                  <Bar dataKey="total" fill="#06b6d4" name="Toplam" />
                  <Bar dataKey="successful" fill="#10b981" name="Başarılı" />
                  <Bar dataKey="cevap_yok" fill="#ef4444" name="Cevap Yok" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* AGENTS TAB */}
      {activeTab === "agents" && overview && (
        <div className="space-y-6">
          <Card className="bg-white border-slate-200">
            <CardHeader><CardTitle>Danışman Karşılaştırması</CardTitle></CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={overview.agent_stats.slice(0, 10)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis type="number" tick={{ fill: '#888' }} />
                    <YAxis dataKey="_id" type="category" tick={{ fill: '#888', fontSize: 11 }} width={120} />
                    <Tooltip contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0' }} />
                    <Bar dataKey="total_calls" fill="#06b6d4" name="Arama" />
                    <Bar dataKey="kayit_ok" fill="#10b981" name="Kayıt" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {overview.agent_stats.slice(0, 9).map((agent) => (
              <motion.div key={agent._id} whileHover={{ scale: 1.02 }} onClick={() => fetchAgentDetail(agent._id)} className="cursor-pointer">
                <Card className="bg-white border-slate-200 hover:border-blue-400">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-slate-800 font-bold">{agent._id?.charAt(0)}</div>
                      <div>
                        <p className="font-medium text-slate-800">{agent._id}</p>
                        <p className="text-xs text-slate-500">{formatNumber(agent.total_calls)} arama</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="p-2 bg-emerald-500/10 rounded"><p className="text-lg font-bold text-emerald-400">{agent.kayit_ok}</p><p className="text-xs text-slate-500">Kayıt</p></div>
                      <div className="p-2 bg-red-500/10 rounded"><p className="text-lg font-bold text-red-400">{agent.cevap_yok}</p><p className="text-xs text-slate-500">Cevapsız</p></div>
                      <div className="p-2 bg-purple-500/10 rounded"><p className="text-lg font-bold text-purple-400">{agent.dusunecek}</p><p className="text-xs text-slate-500">Düşünecek</p></div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {agentDetail && (
            <Card className="bg-white border-cyan-500/50 border-2">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>{agentDetail.agent_name} Detayları</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setAgentDetail(null)}><XCircle size={18} /></Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <div className="text-center p-3 bg-slate-100 rounded-lg"><p className="text-2xl font-bold text-slate-800">{formatNumber(agentDetail.total_calls)}</p><p className="text-xs text-slate-500">Arama</p></div>
                  <div className="text-center p-3 bg-slate-100 rounded-lg"><p className="text-2xl font-bold text-emerald-400">{agentDetail.total_sales}</p><p className="text-xs text-slate-500">Satış</p></div>
                  <div className="text-center p-3 bg-slate-100 rounded-lg"><p className="text-2xl font-bold text-amber-400">{formatCurrency(agentDetail.total_revenue)}</p><p className="text-xs text-slate-500">Ciro</p></div>
                  <div className="text-center p-3 bg-slate-100 rounded-lg"><p className="text-2xl font-bold text-cyan-400">%{agentDetail.total_calls > 0 ? ((agentDetail.status_distribution.find(s => s._id === "KAYIT OK")?.count || 0) / agentDetail.total_calls * 100).toFixed(1) : 0}</p><p className="text-xs text-slate-500">Dönüşüm</p></div>
                </div>
                {agentDetail.recommendations?.length > 0 && (
                  <div className="space-y-2">
                    {agentDetail.recommendations.map((rec, idx) => (
                      <div key={idx} className={`p-3 rounded-lg ${rec.type === "warning" ? "bg-amber-500/10 border border-amber-500/30" : rec.type === "opportunity" ? "bg-purple-500/10 border border-purple-500/30" : "bg-cyan-500/10 border border-cyan-500/30"}`}>
                        <p className="font-medium text-slate-800">{rec.title}</p>
                        <p className="text-sm text-slate-500">{rec.text}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* MESSAGES TAB */}
      {activeTab === "messages" && overview && (
        <div className="space-y-6">
          <Card className="bg-white border-slate-200">
            <CardHeader><CardTitle>Toplu Mesaj Hazırlığı</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {["CEVAP YOK", "DÜŞÜNECEK", "MEŞGUL", "KENDİSİ ARAYACAK OLUMLU"].map(status => {
                  const count = overview.status_distribution.find(s => s._id === status)?.count || 0;
                  return (
                    <Button key={status} variant="outline" onClick={() => fetchBulkMessageList(status)} className="h-auto py-4 flex-col border-slate-300">
                      <span className="text-slate-800 font-medium">{status}</span>
                      <span className="text-2xl font-bold text-cyan-400 mt-1">{formatNumber(count)}</span>
                    </Button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {bulkMessageList && (
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>{bulkMessageList.status} - {formatNumber(bulkMessageList.total)} Kişi</CardTitle>
                  <div className="flex gap-2">
                    <Button onClick={copyPhoneNumbers} className="bg-cyan-600"><Copy size={14} className="mr-1" />Numaraları Kopyala</Button>
                    <Button variant="outline" onClick={() => exportToCSV(bulkMessageList.data, `mesaj_${bulkMessageList.status}`)} className="border-slate-300"><Download size={14} className="mr-1" />CSV</Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-64">
                  <table className="w-full">
                    <thead className="sticky top-0 bg-slate-50">
                      <tr className="text-xs text-slate-500 border-b border-slate-200">
                        <th className="p-2 text-left">Telefon</th>
                        <th className="p-2">Müşteri</th>
                        <th className="p-2">Danışman</th>
                        <th className="p-2">Arama</th>
                        <th className="p-2">Son</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkMessageList.data.map((item, idx) => (
                        <tr key={idx} className="border-b border-slate-200">
                          <td className="p-2 text-cyan-400 font-mono">{item.telefon}</td>
                          <td className="p-2 text-slate-800">{item.customer_name || "-"}</td>
                          <td className="p-2 text-slate-500">{item.agent_name}</td>
                          <td className="p-2 text-center"><span className="px-2 py-0.5 bg-slate-200 rounded">{item.call_count}</span></td>
                          <td className="p-2 text-slate-500 text-sm">{item.last_call?.split("T")[0]}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollArea>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
